import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import api from '../../../../services/api';

interface ITimelineParcel {
  description: string;
  quantity: number;
  delivered: boolean;
  reason: string | null;
  images: string[];
}

interface ITimelineStop {
  id: number;
  ordem: number;
  orderCode: string;
  status: string;
  startHour: string | null;
  endHour: string | null;
  reason: string | null;
  observation: string | null;
  responsibleName: string | null;
  responseDocument: string | null;
  signature: string | null;
  customer: { name: string; city: string | null; state: string | null } | null;
  images: string[];
  parcels: ITimelineParcel[];
}

interface ITimeline {
  routeId: number;
  routeName: string;
  driverName: string | null;
  stops: ITimelineStop[];
}

const ink = '#3A3A3A';
const muted = '#757575';
const good = '#2E7D32';
const bad = '#C62828';

const statusChip = (status: string) => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    delivered: { label: 'ENTREGUE', color: good, bg: '#E8F5E9' },
    undelivered: { label: 'NÃO REALIZADA', color: bad, bg: '#FDEBEA' },
    occurrence: { label: 'OCORRÊNCIA', color: bad, bg: '#FDEBEA' },
    pending: { label: 'PENDENTE', color: muted, bg: '#EEEEEE' },
    'on-route': { label: 'EM ROTA', color: '#0E52A0', bg: '#E3EEF0' },
  };
  const item = map[status] || map.pending;
  return (
    <span
      style={{
        background: item.bg,
        color: item.color,
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 999,
        padding: '3px 10px',
      }}
    >
      {item.label}
    </span>
  );
};

const Thumb = ({ src }: { src: string }) => (
  <a href={src} target="_blank" rel="noreferrer">
    <img
      src={src}
      alt="Foto da entrega"
      style={{
        width: 64,
        height: 64,
        objectFit: 'cover',
        borderRadius: 8,
        border: '1px solid #E0E0E0',
      }}
    />
  </a>
);

export const RouteTimelineDrawer = ({
  routeId,
  onClose,
}: {
  routeId: number | null;
  onClose: () => void;
}) => {
  const [timeline, setTimeline] = useState<ITimeline | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (routeId === null) return;
    setTimeline(null);
    setLoading(true);
    api
      .get(`/route/timeline/${routeId}`)
      .then((response) => setTimeline(response.data))
      .catch(() => setTimeline(null))
      .finally(() => setLoading(false));
  }, [routeId]);

  return (
    <Drawer anchor="right" open={routeId !== null} onClose={onClose}>
      <Box width={480} maxWidth="90vw" p={2.5} role="presentation">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div>
            <Typography style={{ fontSize: 18, fontWeight: 700, color: ink }}>
              {timeline?.routeName || 'Linha do tempo da rota'}
            </Typography>
            {timeline?.driverName && (
              <Typography style={{ fontSize: 13, color: muted }}>
                {timeline.driverName}
              </Typography>
            )}
          </div>
          <IconButton onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && timeline?.stops.length === 0 && (
          <Typography style={{ color: muted, padding: 16 }}>
            Sem paradas nesta rota.
          </Typography>
        )}

        {!loading &&
          timeline?.stops.map((stop) => (
            <Box
              key={stop.id}
              mt={2}
              pb={2}
              borderBottom="1px solid #EEEEEE"
              display="flex"
              gridGap={12}
            >
              <Box
                width={30}
                height={30}
                borderRadius="50%"
                bgcolor="#E3EEF0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                style={{ fontWeight: 700, color: '#0E52A0', fontSize: 13 }}
              >
                {stop.ordem}
              </Box>
              <Box flexGrow={1} minWidth={0}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gridGap={8}
                >
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: ink,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {stop.customer?.name || `Pedido ${stop.orderCode}`}
                  </Typography>
                  {statusChip(stop.status)}
                </Box>
                <Typography style={{ fontSize: 12, color: muted }}>
                  {[
                    stop.customer?.city &&
                      `${stop.customer.city} - ${stop.customer.state || ''}`,
                    stop.startHour && `início ${stop.startHour.slice(0, 5)}`,
                    stop.endHour && `fim ${stop.endHour.slice(0, 5)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>

                {(stop.reason || stop.observation) && (
                  <Typography style={{ fontSize: 12, color: bad }}>
                    {[stop.reason, stop.observation]
                      .filter(Boolean)
                      .join(' — ')}
                  </Typography>
                )}

                {stop.responsibleName && (
                  <Typography style={{ fontSize: 12, color: ink }}>
                    Recebido por <b>{stop.responsibleName}</b>
                    {stop.responseDocument && ` · ${stop.responseDocument}`}
                  </Typography>
                )}

                {(stop.images.length > 0 ||
                  stop.parcels.some((parcel) => parcel.images.length > 0)) && (
                  <Box display="flex" gridGap={8} mt={1} flexWrap="wrap">
                    {stop.images.map((src) => (
                      <Thumb key={src} src={src} />
                    ))}
                    {stop.parcels.flatMap((parcel) =>
                      parcel.images.map((src) => <Thumb key={src} src={src} />)
                    )}
                  </Box>
                )}

                {stop.signature && (
                  <Box mt={1}>
                    <Typography style={{ fontSize: 11, color: muted }}>
                      Assinatura
                    </Typography>
                    <img
                      src={stop.signature}
                      alt="Assinatura do recebedor"
                      style={{
                        width: 180,
                        background: '#FFF',
                        border: '1px solid #E0E0E0',
                        borderRadius: 8,
                      }}
                    />
                  </Box>
                )}

                {stop.parcels.some((parcel) => !parcel.delivered) && (
                  <Typography style={{ fontSize: 12, color: bad, marginTop: 4 }}>
                    Itens não entregues:{' '}
                    {stop.parcels
                      .filter((parcel) => !parcel.delivered)
                      .map(
                        (parcel) =>
                          `${parcel.description}${
                            parcel.reason ? ` (${parcel.reason})` : ''
                          }`
                      )
                      .join(', ')}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
      </Box>
    </Drawer>
  );
};
